import { useState, CSSProperties } from 'react'
import { toast } from 'react-toastify'
import { ToastOptions } from 'react-toastify'
import { FormControl, MenuItem, Select } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import useUser from 'Hooks/useUser'
import useBrand from 'Hooks/useBrand'
import { PlanType } from 'modules/Const'
import Colors from 'modules/Colors'
import SubscriptionStatusIndicator from './SubscriptionStatusIndicator'
import ModalSymphony from 'elements/ModalSymphony'
import ButtonSymphony from 'elements/ButtonSymphony'
import InputSymphony from 'elements/InputSymphony'
import { toastError } from 'modules/Toast'
import Sty from 'modules/Sty'
import useMaestroChat from 'Hooks/useMaestroChat'
import ContentAnalyzerModal from 'components/ContentAnalyzerModal'

export const TOAST_OPTIONS: ToastOptions = {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
}

const AdminOptions = () => {
    const history = useHistory()
    const { isSymphonyAdmin } = useUser()
    const {
        currentBrand,
        admin_createTrialSubscription,
        updateBrandTierPreviewLocally,
        isPaidBrand,
        isPreviewingTier
    } = useBrand()

    const {
        setIsChatOpen
    } = useMaestroChat()

    const isActuallyPaidBrand = isPaidBrand && !isPreviewingTier

    const [showAdminToolsModal, setShowAdminToolsModal] = useState(false)
    const [showContentAnalyzerModal, setShowContentAnalyzerModal] = useState(false)

    const [loading, setLoading] = useState(false)
    const [showTrialDetails, setShowTrialDetails] = useState(false)
    const [showPreviewDetails, setShowPreviewDetails] = useState(false)
    const [selectedPlanType, setSelectedPlanType] = useState<PlanType | null>(PlanType.LITE)
    const [trialDays, setTrialDays] = useState(14)

    const [upgradeEmail, setUpgradeEmail] = useState<string>('')

    if (!currentBrand || !isSymphonyAdmin) {
        return null
    }

    const { subscription } = currentBrand

    const updateSubscription = async (planType: PlanType, isTrialFlow: boolean, trialDays?: number) => {
        setLoading(true)
        try {
            if (isTrialFlow) {
                await admin_createTrialSubscription(currentBrand.id, {
                    planType,
                    trialDays: trialDays ?? undefined,
                    userEmail: upgradeEmail
                })
            } else {
                updateBrandTierPreviewLocally(planType)
            }
            toast.success(`Subscription updated to ${planType} ${!isTrialFlow ? 'until end of session' : ''}.`, TOAST_OPTIONS)
            setShowTrialDetails(false)
            setShowPreviewDetails(false)
        } catch (e) {
            toastError('Error updating subscription')
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    const renderDropdown = (showBgColor = false, showFreeOption = true) => {
        return (
            <FormControl
                variant="outlined"
                size="small"
                fullWidth={true}
                style={{
                    padding: '0 8px',
                    backgroundColor: showBgColor ? Colors.purple : 'transparent'
                }}
            >
                <Select
                    value={selectedPlanType || currentBrand.tier || PlanType.FREE}
                    onChange={(e) => setSelectedPlanType(e.target.value as PlanType)}
                    disabled={loading}
                >
                    {showFreeOption &&
                        <MenuItem value={PlanType.FREE}>Free</MenuItem>
                    }
                    <MenuItem value={PlanType.LITE}>Lite</MenuItem>
                    <MenuItem value={PlanType.PRO}>Pro</MenuItem>
                </Select>
            </FormControl>
        )
    }

    const shouldShowTrialButton = () => {
        if (isPreviewingTier) {
            return true
        }
        return ![PlanType.PRO, PlanType.LITE].includes(currentBrand.tier as PlanType)
    }

    const createSubscriptionClicker = () => {
        const isActiveSubscription = subscription?.status === 'active'
        const isForcedByAdmin = subscription?.details?.forced
        if (subscription && isActiveSubscription && isForcedByAdmin) {
            return (
                <div
                    className="subscriptionStatusContainer"
                    style={{
                        paddingLeft: 16,
                        paddingRight: 16
                    }}
                >
                    <SubscriptionStatusIndicator
                        text="PRO via ADMIN"
                        style={{ marginBottom: 8 }}
                        handleOpenModal={() => {
                            if (subscription.details?.viewSubscriptionUrl) {
                                window.open(subscription.details?.viewSubscriptionUrl!, '_blank')
                            }
                        }}
                    />
                </div>
            )
        }
    }

    const renderButtons = () => {
        return (
            <div className="adminOptionsComponent" style={sty.buttonContainer}>
                {isPreviewingTier &&
                    <ButtonSymphony
                        className="devSubscriptionSpooferPreviewButton"
                        size="small"
                        color="green"
                        onClick={() => setShowAdminToolsModal(true)}
                    >
                        Previewing {currentBrand.tier.toUpperCase()}
                    </ButtonSymphony>
                }

                <ButtonSymphony
                    className="adminToolsButton"
                    onClick={() => setShowAdminToolsModal(true)}
                    variant="outlined"
                    color="white"
                    size="small"
                    width="100%"
                >
                    Admin Tools
                </ButtonSymphony>
            </div>
        )
    }

    return (
        <>
            {createSubscriptionClicker()}
            {isSymphonyAdmin &&
                <div
                    className="adminOptionsContainer flex flex-row items-center mr-auto w-full justify-start"
                    style={{
                        paddingLeft: 16,
                        paddingRight: 16
                    }}
                >
                    {renderButtons()}
                </div>
            }


            <ModalSymphony
                className='adminToolsOptions'
                isOpen={showAdminToolsModal}
                title="Admin Tools"
                onClose={() => setShowAdminToolsModal(false)}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                    <div className='adminToolsOptions_onboarding' style={sty.adminToolsOptionsContent}>
                        <p style={{ ...Sty.text18bold }}>Onboarding & Demos:</p>
                        <div className="buttonWrapperContainer" style={sty.buttonWrapperContainer}>
                            <ButtonSymphony
                                className="adminTeamOnboardingButton"
                                onClick={() => {
                                    history.push('/admin/onboarding')
                                    setShowAdminToolsModal(false)
                                }}
                            >
                                Admin: Onboard a Team
                            </ButtonSymphony>


                            {shouldShowTrialButton() &&
                                <ButtonSymphony
                                    className="devSubscriptionSpooferTrialButton"
                                    color="purpleLight"
                                    onClick={() => setShowTrialDetails(true)}
                                >
                                    Admin: Create Paid Plan Trial
                                </ButtonSymphony>
                            }

                            <ButtonSymphony
                                className="devSubscriptionSpooferPreviewButton"
                                variant="outlined"
                                onClick={() => setShowPreviewDetails(true)}
                            >
                                {isPreviewingTier ?
                                    `Edit Preview Tier (Currently previewing ${currentBrand.tier.toUpperCase()})`
                                    :
                                    'Preview as Different Tier'
                                }
                            </ButtonSymphony>
                        </div>
                    </div>
                    <div className='adminToolsOptions_cs' style={sty.adminToolsOptionsContent}>
                        <div style={Sty.text18bold}>CS Team:</div>
                        <div className="buttonWrapperContainer" style={sty.buttonWrapperContainer}>
                            <ButtonSymphony
                                className='brandEditingToolsButton'
                                onClick={() => {
                                    history.push('/admintools')
                                    setShowAdminToolsModal(false)
                                }}
                                color="green"
                            >
                                Brand Editing Tools
                            </ButtonSymphony>
                            {isActuallyPaidBrand &&
                                <ButtonSymphony
                                    className="viewBillingInfoButton"
                                    variant="outlined"
                                    color="green"
                                    onClick={() => {
                                        history.push('/settings?tab=BILLING')
                                        setShowAdminToolsModal(false)
                                    }}
                                >
                                    View Billing Info
                                </ButtonSymphony>
                            }
                        </div>
                    </div>
                    <div className='partnershipTools' style={sty.adminToolsOptionsContent}>
                        <p style={{ ...Sty.text18bold }}>Partnership Team:</p>
                        <ButtonSymphony
                            className="detailsAdminOptions"
                            variant="outlined"
                            color="green"
                            onClick={() => {
                                history.push('/admin/affiliates')
                                setShowAdminToolsModal(false)
                            }}
                        >
                            View Affiliate Data
                        </ButtonSymphony>
                    </div>

                    <div className='adminToolsOptions_dev' style={sty.adminToolsOptionsContent}>
                        <p style={{ ...Sty.text18bold }}>Dev Team:</p>
                        <div className="buttonWrapperContainer" style={sty.buttonWrapperContainer}>
                            <ButtonSymphony
                                className="allWebsitesButton"
                                onClick={() => {
                                    history.push('/allwebsites')
                                    setShowAdminToolsModal(false)
                                }}
                                variant="outlined"
                                color="grey"
                            >
                                All Websites
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="elementsPageButton"
                                onClick={() => {
                                    history.push('/elementspage')
                                    setShowAdminToolsModal(false)
                                }}
                                variant="outlined"
                                color="grey"
                            >
                                Elements Page
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="instagramContentAnalyzerButton"
                                onClick={() => {
                                    setShowContentAnalyzerModal(true)
                                    setShowAdminToolsModal(false)
                                }}
                                variant="outlined"
                                color="grey"
                            >
                                Instagram Content Analyzer
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="maestroChatButton"
                                onClick={() => {
                                    setIsChatOpen(true)
                                }}
                                variant="outlined"
                            >
                                Open Maestro Chat
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="testApiButton"
                                onClick={() => {
                                    history.push('/testapi')
                                    setShowAdminToolsModal(false)
                                }}
                                variant="outlined"
                                color="grey"
                            >
                                Test API
                            </ButtonSymphony>
                        </div>
                    </div>
                </div>
            </ModalSymphony>

            <ModalSymphony
                className="devSubscriptionSpooferModal"
                size="small"
                title={showTrialDetails ? 'Admin: Set Trial Duration' : 'Admin: Preview as a Different Plan'}
                subtitle={showTrialDetails ?
                    'This will create a Lite or Pro subscription trial for the brand. The profile will be asked to enter card to continue the trial after the trial period.'
                    :
                    'This will temporarily change the subscription plan for testing purposes. No billing changes will be made.'
                }
                isOpen={showTrialDetails || showPreviewDetails}
                onClose={() => {
                    setShowTrialDetails(false)
                    setShowPreviewDetails(false)
                }}
            >
                <div style={sty.modalContent}>
                    <div style={sty.inputContainer}>
                        <p>Plan</p>
                        {renderDropdown(false, !showTrialDetails)}
                    </div>
                    {showTrialDetails &&
                        <>
                            <div style={sty.inputContainer}>
                                <p>Trial Duration</p>
                                <FormControl variant="outlined" size="small" fullWidth style={sty.formControl}>
                                    <Select
                                        value={trialDays}
                                        onChange={(e) => setTrialDays(Number(e.target.value))}
                                        disabled={loading}
                                    >
                                        <MenuItem value={14}>14 Days</MenuItem>
                                        <MenuItem value={30}>30 Days</MenuItem>
                                    </Select>
                                </FormControl>

                            </div>
                            <div style={sty.inputContainer}>
                                <p>User Email</p>
                                <InputSymphony
                                    required
                                    type="email"
                                    placeholder="Enter the primary user's email"
                                    className="devSubscriptionSpooferTrialEmailInput"
                                    value={upgradeEmail}
                                    onChange={(valueText) => setUpgradeEmail(valueText)}
                                    disabled={loading}
                                />
                            </div>
                        </>
                    }
                </div>
                <ButtonSymphony
                    className="devSubscriptionSpooferConfirmButton"
                    isLoading={loading}
                    onClick={() => selectedPlanType && updateSubscription(selectedPlanType, showTrialDetails, trialDays)}
                    variant="outlined"
                    disabled={loading || (!upgradeEmail && showTrialDetails)}
                >
                    {loading ? 'Updating...' : 'Confirm'}
                </ButtonSymphony>
            </ModalSymphony>

            <ContentAnalyzerModal
                isOpen={showContentAnalyzerModal}
                onClose={() => setShowContentAnalyzerModal(false)}
            />
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    buttonContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
        width: '100%'
    },
    inputContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4
    },
    formControl: {
        padding: '0 8px'
    },
    adminToolsOptionsContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    buttonWrapperContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8
    }
}

export default AdminOptions